import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Search, ShoppingCart, FileCheck, IndianRupee, ChevronDown, ChevronRight,
  Receipt, Clock, AlertTriangle, CheckCircle2, FileText, Plus, Wallet, TrendingUp
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const h = React.createElement;

const fmtINR = (n) => "\u20B9" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "\u2014";

const adviceStatusBadge = (status) => {
  const cfg = {
    PENDING: "bg-amber-50 text-amber-700 border-amber-200",
    APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    REJECTED: "bg-rose-50 text-rose-700 border-rose-200",
    RETURNED: "bg-orange-50 text-orange-700 border-orange-200",
  };
  return h("span", { className: `inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg[status] || "bg-slate-100 text-slate-700 border-slate-200"}` }, status);
};

const poStatusBadge = (status) => {
  const cfg = {
    ACTIVE: "bg-blue-50 text-blue-700 border-blue-200",
    DISPATCHED: "bg-cyan-50 text-cyan-700 border-cyan-200",
    IN_TRANSIT: "bg-violet-50 text-violet-700 border-violet-200",
    DELIVERED: "bg-emerald-50 text-emerald-700 border-emerald-200",
    INSTALLED: "bg-teal-50 text-teal-700 border-teal-200",
    COMPLETED: "bg-emerald-100 text-emerald-800 border-emerald-300",
    CANCELLED: "bg-rose-50 text-rose-700 border-rose-200",
    DRAFT: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return h("span", { className: `inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg[status] || cfg.DRAFT}` }, status || "DRAFT");
};

function PaymentRecommendation() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [pos, setPos] = useState([]);
  const [advices, setAdvices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState(""); // "", "PAID", "PARTIAL", "UNPAID"
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [poRes, payRes] = await Promise.all([
        axios.get(`${API_URL}/purchase-orders`, getAuthHeader()),
        axios.get(`${API_URL}/payment-recommendations`, getAuthHeader()),
      ]);
      setPos(poRes.data || []);
      setAdvices(payRes.data || []);
    } catch { toast.error("Failed to load purchase orders"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const advicesByPO = advices.reduce((acc, a) => {
    if (!acc[a.po_id]) acc[a.po_id] = [];
    acc[a.po_id].push(a);
    return acc;
  }, {});

  const computePayStatus = (po) => {
    const list = advicesByPO[po.id] || [];
    const approved = list.filter(a => a.status === "APPROVED");
    const paid = approved.reduce((s, a) => s + (a.invoice_amount || 0), 0);
    const poVal = po.po_value || po.total_amount || 0;
    const pct = poVal > 0 ? Math.round((paid / poVal) * 100) : 0;
    let label = "UNPAID";
    if (pct >= 100) label = "PAID";
    else if (pct > 0) label = "PARTIAL";
    else if (list.some(a => a.status === "PENDING")) label = "PENDING";
    return { paid, balance: Math.max(0, poVal - paid), pct, label, count: list.length };
  };

  const filtered = pos.filter(po => {
    if (search) {
      const s = search.toLowerCase();
      const match = (po.po_number || "").toLowerCase().includes(s) ||
        (po.vendor_details?.name || "").toLowerCase().includes(s) ||
        (po.subject || "").toLowerCase().includes(s);
      if (!match) return false;
    }
    if (statusFilter && po.status !== statusFilter) return false;
    if (paymentFilter) {
      const ps = computePayStatus(po);
      if (ps.label !== paymentFilter) return false;
    }
    return true;
  });

  // Aggregate stats
  const stats = pos.reduce((acc, po) => {
    const ps = computePayStatus(po);
    const poVal = po.po_value || po.total_amount || 0;
    acc.totalPOValue += poVal;
    acc.totalPaid += ps.paid;
    if (ps.label === "PAID") acc.poFullyPaid += 1;
    else if (ps.label === "PARTIAL") acc.poPartial += 1;
    else acc.poUnpaid += 1;
    return acc;
  }, { totalPOValue: 0, totalPaid: 0, poFullyPaid: 0, poPartial: 0, poUnpaid: 0 });
  stats.totalPending = advices.filter(a => a.status === "PENDING").length;

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const renderPORow = (po) => {
    const ps = computePayStatus(po);
    const poVal = po.po_value || po.total_amount || 0;
    const list = advicesByPO[po.id] || [];
    const isOpen = !!expanded[po.id];

    const rows = [
      h(TableRow, {
        key: po.id,
        className: "hover:bg-slate-50/60 cursor-pointer",
        onClick: () => toggle(po.id),
        "data-testid": "po-row-" + po.id
      },
        h(TableCell, { className: "w-8" },
          h("button", { className: "p-0.5 hover:bg-slate-200 rounded", onClick: e => { e.stopPropagation(); toggle(po.id); } },
            isOpen ? h(ChevronDown, { className: "w-4 h-4 text-slate-500" }) : h(ChevronRight, { className: "w-4 h-4 text-slate-500" })
          )
        ),
        h(TableCell, null,
          h("div", { className: "font-mono font-semibold text-sm text-slate-900" }, po.po_number || "—"),
          h("div", { className: "text-xs text-slate-400 mt-0.5" }, fmtDate(po.po_date || po.created_at))
        ),
        h(TableCell, null,
          h("div", { className: "font-medium text-sm text-slate-900" }, po.vendor_details?.name || "\u2014"),
          po.vendor_details?.gst_number && h("div", { className: "text-xs text-slate-400 font-mono" }, po.vendor_details.gst_number)
        ),
        h(TableCell, null,
          h(Badge, { variant: "outline", className: "text-xs whitespace-nowrap" }, (po.po_type || "DOMESTIC") + (po.po_sub_type ? " / " + po.po_sub_type : ""))
        ),
        h(TableCell, { className: "text-right" },
          h("div", { className: "font-bold text-sm text-slate-900" }, fmtINR(poVal))
        ),
        h(TableCell, null, poStatusBadge(po.status)),
        h(TableCell, null,
          // Payment progress bar
          h("div", { className: "min-w-[120px]" },
            h("div", { className: "flex items-center justify-between text-xs mb-1" },
              h("span", { className: "font-semibold " + (ps.pct >= 100 ? "text-emerald-700" : ps.pct > 0 ? "text-amber-700" : "text-slate-500") }, ps.pct + "%"),
              h("span", { className: "text-slate-400" }, list.length, " advice", list.length !== 1 ? "s" : "")
            ),
            h("div", { className: "h-1.5 bg-slate-100 rounded-full overflow-hidden" },
              h("div", {
                className: (ps.pct >= 100 ? "bg-emerald-500" : ps.pct > 0 ? "bg-amber-500" : "bg-slate-200") + " h-full rounded-full transition-all",
                style: { width: Math.min(100, ps.pct) + "%" }
              })
            )
          )
        ),
        h(TableCell, { className: "text-right text-xs" },
          h("div", { className: "font-semibold text-emerald-700" }, fmtINR(ps.paid)),
          h("div", { className: "text-slate-400" }, "Bal: ", fmtINR(ps.balance))
        ),
        h(TableCell, { className: "text-right" },
          h(Button, {
            size: "sm",
            className: "bg-slate-900 hover:bg-slate-800 text-white text-xs h-8",
            onClick: e => { e.stopPropagation(); navigate(`/payments/new?po=${po.id}`); },
            disabled: ps.label === "PAID" || po.status === "CANCELLED",
            "data-testid": "create-advice-" + po.id
          },
            h(Plus, { className: "w-3.5 h-3.5 mr-1" }),
            "Payment Advice"
          )
        )
      )
    ];

    // Expanded row showing existing advices
    if (isOpen) {
      rows.push(
        h(TableRow, { key: po.id + "-exp", className: "bg-slate-50/40 hover:bg-slate-50/40" },
          h(TableCell, { colSpan: 9, className: "p-0" },
            h("div", { className: "p-4" },
              h("div", { className: "flex items-center justify-between mb-3" },
                h("div", { className: "flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-slate-600" },
                  h(Receipt, { className: "w-3.5 h-3.5" }),
                  "Payment Advice History (", list.length, ")"
                ),
                po.subject && h("p", { className: "text-xs text-slate-500 italic max-w-md truncate" }, "Subject: " + po.subject)
              ),
              list.length > 0 ?
                h("div", { className: "border rounded-lg overflow-hidden bg-white" },
                  h(Table, { className: "text-sm" },
                    h(TableHeader, null, h(TableRow, { className: "bg-slate-50/80 hover:bg-slate-50/80" },
                      h(TableHead, { className: "h-8 text-xs" }, "Advice #"),
                      h(TableHead, { className: "h-8 text-xs" }, "Item"),
                      h(TableHead, { className: "h-8 text-xs" }, "Invoice"),
                      h(TableHead, { className: "h-8 text-xs" }, "Date Recvd"),
                      h(TableHead, { className: "h-8 text-xs" }, "Type"),
                      h(TableHead, { className: "h-8 text-xs text-right" }, "Amount"),
                      h(TableHead, { className: "h-8 text-xs" }, "Status")
                    )),
                    h(TableBody, null, list.map(a => h(TableRow, { key: a.id, className: "hover:bg-slate-50/40" },
                      h(TableCell, { className: "font-mono text-xs font-semibold py-2" }, a.recommendation_number),
                      h(TableCell, { className: "text-xs py-2 max-w-[220px]" },
                        h("div", { className: "truncate font-medium" }, a.item_name || "\u2014"),
                        (a.grn_category || a.grn_subcategory) && h("div", { className: "text-[10px] text-slate-500 truncate" },
                          [a.grn_category, a.grn_subcategory].filter(Boolean).join(" \u2022 ")
                        )
                      ),
                      h(TableCell, { className: "text-xs py-2 font-mono" }, a.invoice_number || "\u2014"),
                      h(TableCell, { className: "text-xs py-2 text-slate-500" }, fmtDate(a.date_received)),
                      h(TableCell, { className: "py-2" },
                        h("div", { className: "flex flex-wrap items-center gap-1" },
                          h(Badge, { variant: "outline", className: "text-[10px]" },
                            a.payment_type === "FULL" ? "Full" : `Part ${a.part_payment_percentage || ""}%`),
                          a.ld_applicable && h(Badge, { className: "text-[10px] bg-rose-50 text-rose-700 border border-rose-200" }, "LD"),
                          a.ld_waived && h(Badge, { className: "text-[10px] bg-amber-50 text-amber-700 border border-amber-200" }, "LD Waived")
                        )
                      ),
                      h(TableCell, { className: "text-right text-xs font-bold py-2" }, fmtINR(a.invoice_amount)),
                      h(TableCell, { className: "py-2" }, adviceStatusBadge(a.status))
                    )))
                  )
                ) :
                h("div", { className: "p-6 text-center bg-white border border-dashed border-slate-200 rounded-lg" },
                  h(FileText, { className: "w-8 h-8 text-slate-300 mx-auto mb-2" }),
                  h("p", { className: "text-xs text-slate-500" }, "No payment advice raised against this PO yet"),
                  h(Button, {
                    size: "sm", variant: "outline", className: "mt-3 text-xs",
                    onClick: () => navigate(`/payments/new?po=${po.id}`)
                  }, h(Plus, { className: "w-3.5 h-3.5 mr-1" }), "Create First Advice")
                )
            )
          )
        )
      );
    }
    return rows;
  };

  return h("div", { className: "space-y-6", "data-testid": "payment-advice-page" },
    // Header
    h("div", { className: "flex flex-col md:flex-row md:items-center md:justify-between gap-4" },
      h("div", null,
        h("h1", { className: "text-2xl font-bold text-slate-900 tracking-tight" }, "Payment Advice"),
        h("p", { className: "text-slate-500 mt-1" }, "Raise a payment advice against any approved Purchase Order \u2014 partial or full")
      )
    ),

    // Stats
    h("div", { className: "grid grid-cols-2 md:grid-cols-5 gap-3" },
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(ShoppingCart, { className: "w-3.5 h-3.5" }), "Total POs"),
        h("p", { className: "text-2xl font-bold text-slate-900 mt-1" }, pos.length)
      ),
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(CheckCircle2, { className: "w-3.5 h-3.5" }), "Fully Paid"),
        h("p", { className: "text-2xl font-bold text-emerald-700 mt-1" }, stats.poFullyPaid)
      ),
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(Clock, { className: "w-3.5 h-3.5" }), "Partial"),
        h("p", { className: "text-2xl font-bold text-amber-700 mt-1" }, stats.poPartial)
      ),
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(AlertTriangle, { className: "w-3.5 h-3.5" }), "Pending"),
        h("p", { className: "text-2xl font-bold text-orange-700 mt-1" }, stats.totalPending)
      ),
      h(Card, { className: "p-4" },
        h("div", { className: "flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider" }, h(Wallet, { className: "w-3.5 h-3.5" }), "Paid / Total"),
        h("p", { className: "text-base font-bold text-slate-900 mt-1 leading-tight" }, fmtINR(stats.totalPaid)),
        h("p", { className: "text-xs text-slate-400" }, "of ", fmtINR(stats.totalPOValue))
      )
    ),

    // Filters
    h(Card, null, h(CardContent, { className: "p-4" },
      h("div", { className: "flex flex-col md:flex-row gap-3" },
        h("div", { className: "relative flex-1" },
          h(Search, { className: "absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" }),
          h(Input, { placeholder: "Search by PO number, vendor name, subject...", value: search, onChange: e => setSearch(e.target.value), className: "pl-10", "data-testid": "po-search" })
        ),
        h("select", { value: statusFilter, onChange: e => setStatusFilter(e.target.value), className: "h-10 px-3 rounded-md border border-slate-200 text-sm bg-white min-w-[150px]" },
          h("option", { value: "" }, "All PO Status"),
          ["ACTIVE", "DISPATCHED", "IN_TRANSIT", "DELIVERED", "INSTALLED", "COMPLETED", "CANCELLED"].map(s => h("option", { key: s, value: s }, s))
        ),
        h("select", { value: paymentFilter, onChange: e => setPaymentFilter(e.target.value), className: "h-10 px-3 rounded-md border border-slate-200 text-sm bg-white min-w-[150px]" },
          h("option", { value: "" }, "All Payment Status"),
          h("option", { value: "UNPAID" }, "Unpaid"),
          h("option", { value: "PARTIAL" }, "Partially Paid"),
          h("option", { value: "PAID" }, "Fully Paid"),
          h("option", { value: "PENDING" }, "Pending Approval")
        )
      )
    )),

    // Main Table
    h(Card, null, h(CardContent, { className: "p-0" },
      loading ? h("div", { className: "p-12 text-center text-slate-400" }, "Loading purchase orders...") :
      filtered.length > 0 ? h("div", { className: "overflow-x-auto" }, h(Table, null,
        h(TableHeader, null, h(TableRow, { className: "bg-slate-50/80 hover:bg-slate-50/80" },
          h(TableHead, { className: "w-8" }),
          h(TableHead, { className: "font-semibold" }, "PO Number"),
          h(TableHead, { className: "font-semibold" }, "Vendor"),
          h(TableHead, { className: "font-semibold" }, "Type"),
          h(TableHead, { className: "font-semibold text-right" }, "PO Value"),
          h(TableHead, { className: "font-semibold" }, "PO Status"),
          h(TableHead, { className: "font-semibold" }, "Payment Progress"),
          h(TableHead, { className: "font-semibold text-right" }, "Paid / Bal"),
          h(TableHead, { className: "w-44 text-right" }, "Action")
        )),
        h(TableBody, null, filtered.flatMap(renderPORow))
      )) : h("div", { className: "p-16 text-center" },
        h(ShoppingCart, { className: "w-14 h-14 text-slate-200 mx-auto mb-4" }),
        h("p", { className: "text-slate-600 font-medium" }, pos.length === 0 ? "No purchase orders found" : "No POs match your filter"),
        h("p", { className: "text-sm text-slate-400 mt-1" }, pos.length === 0 ? "Create a Purchase Order first to raise payment advice" : "Try a different search or filter")
      )
    ))
  );
}

export default PaymentRecommendation;
